import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RoutesRecognized } from "@angular/router";
import { AppCommonService } from "../app-common.service";
import { SharedService, TurnstileApi, TurnstileService } from "src/app/utils/services";
import { ForgotPasswordV2Component } from "../forgot-password-v2/forgot-password-v2.component";
import { environment } from "src/environments/environment";
import { NoopScrollStrategy } from "@angular/cdk/overlay";
import { AccountLockComponent } from "../account-lock/account-lock.component";
import { filter, pairwise, switchMap } from "rxjs/operators";
import { SessionStorage, SessionStorageService } from "ngx-webstorage";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialog } from "@angular/material/dialog";
import { PrivacyDialogComponent } from "src/app/utils/components/privacy-dialog/privacy-dialog.component";
@Component({
    selector: "app-login-v2",
    templateUrl: "./login-v2.component.html",
    styleUrls: ["./login-v2.component.scss"],
    standalone: false
})
export class LoginV2Component implements OnInit, AfterViewInit, OnDestroy {
  @SessionStorage("auth") public authToken;
  @SessionStorage("mobileForOtp") public mobileForOtp: any;
  @SessionStorage("otp") public otp: any;
  @SessionStorage("whichPage") public whichPage: any;
  public loginForm: UntypedFormGroup;
  public showPassword = false;
  public siteKey = environment.siteKey;
  public submitted = false;
  public submitting = false;
  public captchaMessage = "";
  // Cloudflare Turnstile token; still sent to the backend in the `recaptcha` field
  public recaptcha = "";
  public recentError?: { error: any };
  @ViewChild("turnstile") private turnstileContainer: ElementRef<HTMLElement>;
  private turnstile?: TurnstileApi;
  private turnstileWidgetId?: string;

  constructor(
    private appCommonService: AppCommonService,
    private sharedService: SharedService,
    public formBuilder: UntypedFormBuilder,
    private router: Router,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private sessionStorageService: SessionStorageService,
    public activatedRoute: ActivatedRoute,
    private turnstileService: TurnstileService,
    private zone: NgZone,
    private host: ElementRef<HTMLElement>
  ) {
    if (this.authToken && this.router.url.includes("login")) {
      this.sessionStorageService.clear();
      sessionStorage.clear();
    }

    this.loginForm = this.formBuilder.group({
      mobile: ["", [Validators.required, Validators.pattern(/^\d{10}$/)]],
      password: ["", [Validators.required, Validators.minLength(10)]],
      // recaptcha: ["", [Validators.required]],
      checkbox: [false, [Validators.requiredTrue]],
      // cb: [false, Validators.requiredTrue]
    });
    this.whichPage = "no-page";
  }

  ngAfterViewInit() {
    this.turnstileService
      .load()
      .then((turnstile) => {
        this.turnstile = turnstile;
        this.turnstileWidgetId = turnstile.render(this.turnstileContainer.nativeElement, {
          sitekey: this.siteKey,
          action: "login",
          // Always show the managed widget; Cloudflare decides whether the user must tick the box
          appearance: "always",
          // Same width as the form fields
          size: "flexible",
          callback: (token) =>
            this.zone.run(() => {
              this.recaptcha = token;
              this.recentError = undefined;
              this.captchaMessage = "";
            }),
          "expired-callback": () => this.zone.run(() => this.resetCaptcha()),
          "error-callback": (code) =>
            this.zone.run(() => {
              this.recaptcha = "";
              this.recentError = { error: code };
              this.captchaMessage = "The security check couldn't complete. Refresh the page and try again.";
            }),
        });
      })
      .catch((error) => {
        this.recentError = { error };
        this.captchaMessage = "The security check couldn't load. Check your connection and refresh the page.";
      });
  }

  /* Turnstile tokens are single use and expire after 5 minutes, so get a new one after each attempt */
  private resetCaptcha() {
    this.recaptcha = "";
    if (this.turnstile && this.turnstileWidgetId) {
      this.turnstile.reset(this.turnstileWidgetId);
    }
  }

  ngOnInit() {
    if (this.router.url.includes("login")) {
      this.router.events
        .pipe(
          filter((evt: any) => evt instanceof RoutesRecognized),
          pairwise()
        )
        .subscribe((events: RoutesRecognized[]) => {
          if (
            events[1].urlAfterRedirects.includes("login") &&
            !events[0].urlAfterRedirects.includes("login") &&
            this.authToken &&
            this.authToken.token
          ) {
            window.history.forward();
          }
        });
    }
    sessionStorage.removeItem("mobileForOtp");
    this.sessionStorageService.clear("mobileForOtp");
  }
  /* Errors show once a field has been edited or the form has been submitted */
  showError(name: string): boolean {
    const control = this.loginForm.get(name);
    return !!control && control.invalid && (this.submitted || control.touched || control.dirty);
  }

  openAccountlockDialog() {
    const dialogRef = this.dialog.open(AccountLockComponent, {
      panelClass: "dialog-box", // Add your custom panel class
      scrollStrategy: new NoopScrollStrategy(),
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }
  openDialog() {
    const dialogRef = this.dialog.open(ForgotPasswordV2Component, {
      panelClass: "dialog-box", // Add your custom panel class
      scrollStrategy: new NoopScrollStrategy(),
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }

  /* To show or hide the password by changing input type to 'text' to 'password' and vice-versa */
  toggleInputType() {
    this.showPassword = !this.showPassword;
  }

  LoginStep2() {
    this.submitting = true;
    this.sharedService.display(true);
    const loginData = this.loginForm.value;
    delete loginData["recaptcha"];
    const body = {
      phone: loginData.mobile,
      password: loginData.password,
      recaptcha: this.recaptcha,
    };
    this.resetCaptcha();
    this.appCommonService.post("login", body).subscribe(
      (response) => {
        this.submitting = false;
        this.sharedService.display(false);
        if (response.success === true) {
          let number = "*****" + this.loginForm.value.mobile.substring(6, 10);
          this.sharedService.openSnackBar({
            success: `OTP has been sent to "${number}"`,
          });

          this.appCommonService.setData("mobile", this.loginForm.value.mobile);
          this.mobileForOtp = this.loginForm.value.mobile;
          // sessionStorage.setItem('mobileForOtp', `${this.loginForm.value.mobile}`);
          this.sharedService.whichPage = "login";
          this.whichPage = "login";
          this.otp = response.payload.otp;
          // this.whichPage='/otp-verify'
          this.router.navigateByUrl("/otp-verify");
        } else {
          this.sharedService.openSnackBar({ error: "Invalid Credentials" });
        }
      },
      (err) => {
        this.submitting = false;
        if (err.block === true) {
          this.sharedService.display(false);
          this.openAccountlockDialog();
        } else {
          this.sharedService.display(false);
          this.sharedService.openSnackBar({
            error: err.hasOwnProperty("error")
              ? err.error.message
              : "Could not connect to server",
          });
        }
      }
    );
  }

  onLogin() {
    if (this.submitting) return;
    this.submitted = true;
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      // Move focus to the first field that needs attention
      const firstInvalid = this.host.nativeElement.querySelector<HTMLElement>("input[aria-invalid='true'], input.ng-invalid");
      if (firstInvalid) firstInvalid.focus();
      return;
    }
    if (!this.recaptcha) {
      this.captchaMessage = "Complete the security check above, then select Login.";
      return;
    }
    this.LoginStep2();
  }
  filterPopUp(id) {
    let obj = {
      id: id,
      selectedTab: "associates",
    };
    const dialogRef = this.dialog.open(PrivacyDialogComponent, {
      width: "auto",
      height: "90vh",
      data: obj,
      backdropClass: "transaction-associate-backdrop",
      panelClass: "transaction-associate-dialog",
      autoFocus: false,
    });
  }
  ngOnDestroy() {
    if (this.turnstile && this.turnstileWidgetId) {
      this.turnstile.remove(this.turnstileWidgetId);
    }
  }
}
